import classNames from "classnames";
import MUIChip, { ChipProps } from "@mui/material/Chip";
import { TooltipProps } from "@mui/material/Tooltip";
import React, { ReactNode } from "react";

import Tooltip from "./Tooltip";

type DSChipProps = Omit<ChipProps, "label"> & {
    red?: boolean;
    green?: boolean;
    label: React.ReactNode;
    className?: string | null;
};

const DSChip = React.forwardRef<HTMLDivElement, DSChipProps>(
    ({ className, red = false, green = false, label, ...props }: DSChipProps, ref) => (
        <MUIChip
            className={classNames(className, { "chip--red": red, "chip--green": green })}
            label={label}
            ref={ref}
            {...props}
        />
    ),
);

export const Chip = React.forwardRef<HTMLDivElement, DSChipProps>((props: DSChipProps, ref) => (
    <DSChip {...props} ref={ref} />
));

export interface OverflowChipProps extends DSChipProps {
    maxLength?: number;
    placement?: TooltipProps["placement"];
    tooltip?: ReactNode | string[];
}
/*
 * show the tooltip on hover only if the text isn’t > 20 characters long.
 * If the text is long than we show the full label on the hover and not the attribute.
 */
export const OverflowChip = React.forwardRef<HTMLDivElement, OverflowChipProps>(
    ({ label, tooltip, maxLength = 20, placement = "top", ...props }: OverflowChipProps, ref) => {
        const isTooLong = maxLength && typeof label === "string" && label.length > maxLength;
        const localLabel = isTooLong ? `${label.substring(0, maxLength)}...` : label;
        const chipTooltip = Array.isArray(tooltip)
            ? tooltip.map((tt, i) => <div key={`chip-tooltip-${i}`}>{tt}</div>)
            : tooltip;
        const toolTipLabel = isTooLong ? label : chipTooltip;
        return (
            <Tooltip
                title={toolTipLabel || ""}
                disableHoverListener={!(isTooLong || tooltip)}
                placement={placement}
            >
                <DSChip label={localLabel} ref={ref} {...props} />
            </Tooltip>
        );
    },
);

export const SmallChip = React.forwardRef<HTMLDivElement, OverflowChipProps>(
    (props: OverflowChipProps, ref) => <OverflowChip size="small" {...props} ref={ref} />,
);
export const OutlinedChip = React.forwardRef<HTMLDivElement, OverflowChipProps>(
    (props: OverflowChipProps, ref) => <OverflowChip variant="outlined" {...props} ref={ref} />,
);
export const SmallOutlinedChip = React.forwardRef<HTMLDivElement, OverflowChipProps>(
    (props: OverflowChipProps, ref) => <OutlinedChip size="small" {...props} ref={ref} />,
);

/**
 * For displaying a list of chips with padding, alignment, etc, etc.
 */
type DSChipListProps = JSX.IntrinsicElements["div"] & {
    alignRight?: boolean;
};

export const ChipList = ({
    alignRight = false,
    children,
    className,
    ...props
}: DSChipListProps) => (
    <div
        className={classNames(
            "ws-chip-list",
            {
                "ws-chip-list--right": alignRight,
            },
            className,
        )}
        {...props}
    >
        {children}
    </div>
);

export default Chip;
